import React, { useState, useEffect } from "react";
import ErpDescription from "./ErpDescription";
import { useModal } from "../contexts/ModalContext";
import OtimizeSeuNegocio from "./OtimizeSeuNegocio";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "./carousel";

// Card component for benefits
const BenefitCard = ({ icon, title, text, className, height, onCardExpand, isActive }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isExpanded, setIsExpanded] = useState(isActive || false);
    const [isMobile, setIsMobile] = useState(false);

    // Check if we're on mobile when component mounts and when window resizes
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        // Initial check
        checkIfMobile();

        // Add event listener for window resize
        window.addEventListener("resize", checkIfMobile);

        // Cleanup
        return () => window.removeEventListener("resize", checkIfMobile);
    }, []);
    
    // Update expanded state when isActive prop changes
    useEffect(() => {
        setIsExpanded(isActive || false);
    }, [isActive]);

    function handleMouseEnter() {
        setIsHovered(true);
    }

    function handleMouseLeave() {
        setIsHovered(false);
    }

    function handleCardClick() {
        // Only toggle expansion on mobile devices
        if (isMobile) {
            const newExpandedState = !isExpanded;
            setIsExpanded(newExpandedState);
            if (onCardExpand && newExpandedState) {
                onCardExpand();
            }
        }
    }

    return (
        <div
            className={`relative hover:cursor-pointer ${className}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleMouseEnter}
            onTouchEnd={handleMouseLeave}
            onClick={handleCardClick}
        >
            {/* Card with sharp white borders */}
            <div className={`relative bg-[#FFFFFF17] rounded-lg border-1 border-white/80 overflow-hidden ${isMobile ? 'h-auto' : `h-[${height}]`}`}>
                {/* Purple blur effect on hover - contained within the card */}
                <div
                    className={`absolute top-0 right-0 w-36 h-36 transition-opacity duration-300 blur-3xl ${
                        isHovered ? "opacity-100" : "opacity-0"
                    }`}
                    style={{ backgroundColor: "#4B3BDB" }}
                />

                {/* Content container - positioned above the blur */}
                <div className="relative z-10 p-5 md:p-8 h-full flex flex-col">
                    {/* Header with icon and title side by side */}
                    <div className="flex items-center justify-between mb-0 md:mb-6">
                        <div className="flex items-center gap-4">
                            {/* Icon */}
                            <div className="relative w-14 h-14">
                                <div
                                    className={`absolute inset-0 rounded-full ${
                                        isHovered || isExpanded
                                            ? "bg-gradient-to-r from-[#4B3BDB] via-[#4B3BDB] to-[#A002CB] opacity-100"
                                            : "bg-white/10 blur-[2px]"
                                    }`}
                                />
                                <div className="absolute inset-[2px] rounded-full bg-[#252237] flex items-center justify-center">
                                    <img
                                        src={icon}
                                        alt={title}
                                        className="w-7 h-7"
                                    />
                                </div>
                            </div>

                            {/* Title */}
                            <h3 className="text-xl md:text-2xl font-inter-bold text-white">
                                {title}
                            </h3>
                        </div>

                        {/* Mobile-only expand/collapse arrow indicator */}
                        <div className="md:hidden text-white">
                            {isExpanded ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="m18 15-6-6-6 6" />
                                </svg>
                            ) : (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="m6 9 6 6 6-6" />
                                </svg>
                            )}
                        </div>
                    </div>

                    {/* Text content */}
                    <div
                        className={`text-base md:text-xl text-white font-inter-regular leading-relaxed transition-all duration-300 ease-in-out ${
                            isMobile && !isExpanded
                                ? "max-h-0 opacity-0 overflow-hidden transform -translate-y-[10px]"
                                : "opacity-100 transform translate-y-0"
                        } ${!isMobile ? 'flex-grow' : ''}`}
                        style={{
                            transitionProperty: 'max-height, opacity, transform'
                        }}
                    >
                        <p className="pt-6 md:pt-0">{text}</p>
                    </div>
                </div>
            </div>
            
            {/* OtimizeSeuNegocio component that appears below the card on hover (desktop only) or when expanded (mobile) */}
            {/* On desktop, we always render the div but control visibility with opacity */}
            {isMobile ? (
                // Mobile: Always render but use height and opacity transitions for smooth animation
                <div 
                    className={`transition-all duration-300 ease-in-out transform mt-0 w-full overflow-hidden
                        ${isExpanded ? 'max-h-[100px] opacity-100' : 'max-h-0 opacity-0'}`}
                    style={{
                        transitionProperty: 'max-height, opacity, transform',
                        transform: isExpanded ? 'translateY(0)' : 'translateY(-10px)'
                    }}
                >
                    <OtimizeSeuNegocio 
                        text="Otimize seu negócio!"
                        buttonText="Contratar"
                        useFormularioRD={true}
                        type="gradient"
                        className="w-full mt-4"
                    />
                </div>
            ) : (
                // Desktop: Always render but control visibility with opacity
                <div 
                    className={`transition-all duration-300 transform mt-4 w-full ${isHovered ? 'opacity-100' : 'opacity-0'}`}
                    onMouseEnter={handleMouseEnter}
                    style={{ height: '80px' }} // Fixed height to reserve space
                >
                    <OtimizeSeuNegocio 
                        text="Otimize seu negócio!"
                        buttonText="Contratar"
                        useFormularioRD={true}
                        type="gradient"
                        className="w-full"
                    />
                </div>
            )}
        </div>
    );
};

function ErpExpertiseBrand({ logo, description, bg, cards }) {
    const [isMobile, setIsMobile] = useState(false);
    const [activeCardIndex, setActiveCardIndex] = useState(null);
    
    // Check if we're on mobile when component mounts and when window resizes
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        // Initial check
        checkIfMobile();

        // Add event listener for window resize
        window.addEventListener("resize", checkIfMobile);

        // Cleanup
        return () => window.removeEventListener("resize", checkIfMobile);
    }, []);
    
    // Only use carousel on desktop and when more than 3 cards
    const useCarousel = !isMobile && cards && cards.length > 3;

    return (
        <div className={`w-full text-2xl py-16 px-8 lg:px-32 ${bg}`}>
            <ErpDescription logo={logo} description={description} />

            {cards && cards.length > 0 && (
                <div className=" mx-auto pt-8 md:pt-24 lg:px-18">
                    {/* Desktop view with carousel when needed */}
                    {!isMobile && useCarousel ? (
                        <div className="relative mx-[-1rem] md:mx-[-2rem] hidden md:block">
                            <Carousel className="overflow-visible">
                                <div className="flex justify-end absolute right-6 md:right-12 z-10 text-white">
                                    <CarouselPrevious className="h-8 w-8 rounded-full bg-transparent hover:cursor-pointer hover:scale-130 transition-transform duration-300" />
                                    <CarouselNext className="h-8 w-8 rounded-full bg-transparent hover:cursor-pointer hover:scale-130 transition-transform duration-300" />
                                </div>
                                <CarouselContent className="pt-6 gap-0 pl-4">
                                    {cards.map((card, index) => (
                                        <CarouselItem
                                            key={index}
                                            className="basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/3 "
                                        >
                                            <div className="relative overflow-visible pb-20">
                                                <BenefitCard
                                                    icon={card.icon}
                                                    title={card.title}
                                                    text={card.text}
                                                    className="w-full"
                                                    height={card.height}
                                                    isActive={activeCardIndex === index}
                                                    onCardExpand={() => setActiveCardIndex(index)}
                                                />
                                            </div>
                                        </CarouselItem>
                                    ))}
                                </CarouselContent>
                            </Carousel>
                        </div>
                    ) : null}
                    
                    {/* Mobile view always uses grid layout OR desktop view when 3 or fewer cards */}
                    {(isMobile || !useCarousel) && (
                        <div className={`grid grid-cols-1 ${isMobile ? '' : 'md:grid-cols-2 lg:grid-cols-3'} gap-4`}>
                            {cards.map((card, index) => (
                                <BenefitCard
                                    key={index}
                                    icon={card.icon}
                                    title={card.title}
                                    text={card.text}
                                    className="w-full"
                                    height={card.height}
                                    isActive={activeCardIndex === index}
                                    onCardExpand={() => setActiveCardIndex(index)}
                                />
                            ))}
                        </div>
                    )}
                    {/* Global OtimizeSeuNegocio removed as it's now shown per card */}
                </div>
            )}

            {logo === "../assets/erp-marcas-1.svg" && (
                <div className="flex">
                    <div className="hidden md:block w-1/3"></div>
                    <div className="w-full md:w-2/3">
                        <div className={`pt-0 md:pt-4 ${isMobile ? 'w-full px-0 md:px-4' : 'md:w-[520px] ml-4 px-20'} mt-4`}>
                            <OtimizeSeuNegocio 
                                text="Otimize seu negócio!"
                                buttonText="Contratar"
                                useFormularioRD={true}
                                type="gradient"
                                className="mt-0 w-full"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ErpExpertiseBrand;
